import { Loader2, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ChatThread } from "../types";
import { AIChatHistoryItem } from "./AIChatHistoryItem";

const HISTORY_PAGE_SIZE = 10;

type Props = {
  threads: ChatThread[];
  activeThreadId: string | null;
  historyQuery: string;
  onHistoryQueryChange: (value: string) => void;
  onSelectThread: (id: string) => void;
  onNewChat: () => void;
  isHistoryLoading?: boolean;
};

export function AIChatSidebar({
  threads,
  activeThreadId,
  historyQuery,
  onHistoryQueryChange,
  onSelectThread,
  onNewChat,
  isHistoryLoading = false,
}: Props) {
  const q = historyQuery.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? threads.filter((t) => t.title.toLowerCase().includes(q))
        : threads,
    [threads, q],
  );

  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(HISTORY_PAGE_SIZE);
  }, [historyQuery, threads]);

  const orderedFiltered = useMemo(() => {
    const todayItems = filtered.filter((t) => t.group === "today");
    const oldItems = filtered.filter((t) => t.group === "old");
    return [...todayItems, ...oldItems];
  }, [filtered]);

  const visibleThreads = orderedFiltered.slice(0, visibleCount);
  const today = visibleThreads.filter((t) => t.group === "today");
  const old = visibleThreads.filter((t) => t.group === "old");
  const hasMore = visibleCount < orderedFiltered.length;

  return (
    <aside className="hidden h-full w-[260px] shrink-0 flex-col border-r border-[#E5E7EB] bg-[#EEF2F6] p-3 md:flex">
      <button
        type="button"
        onClick={onNewChat}
        className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-primary text-sm font-medium text-white shadow-sm hover:bg-primary/90"
      >
        <Plus className="h-4 w-4" />
        New Chat
      </button>

      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
        <input
          value={historyQuery}
          onChange={(e) => onHistoryQueryChange(e.target.value)}
          placeholder="Search history..."
          className="h-9 w-full rounded-lg border border-[#E5E7EB] bg-white pl-9 pr-3 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-primary/40"
        />
      </div>

      <div className="mt-4 min-h-0 flex-1 space-y-5 overflow-y-auto pr-0.5">
        {isHistoryLoading ? (
          <div className="space-y-3 px-2">
            <div className="flex items-center gap-2 text-sm font-medium text-[#6B7280]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading chat history…
            </div>
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-10 w-full animate-pulse rounded-lg border border-[#E5E7EB] bg-white"
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <HistoryGroup
              label="Today"
              threads={today}
              activeThreadId={activeThreadId}
              onSelectThread={onSelectThread}
            />
            <HistoryGroup
              label="Old"
              threads={old}
              activeThreadId={activeThreadId}
              onSelectThread={onSelectThread}
            />
            {hasMore ? (
              <button
                type="button"
                onClick={() =>
                  setVisibleCount((count) =>
                    Math.min(count + HISTORY_PAGE_SIZE, orderedFiltered.length),
                  )
                }
                className="mt-1 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-medium text-primary hover:bg-[#F9FAFB]"
              >
                Show more
              </button>
            ) : null}
            {!isHistoryLoading && orderedFiltered.length === 0 ? (
              <p className="px-2 text-sm text-[#9CA3AF]">No chats found.</p>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
}

function HistoryGroup({
  label,
  threads,
  activeThreadId,
  onSelectThread,
}: {
  label: string;
  threads: ChatThread[];
  activeThreadId: string | null;
  onSelectThread: (id: string) => void;
}) {
  if (threads.length === 0) return null;

  return (
    <div className="space-y-1">
      <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
        {label}
      </p>
      {threads.map((thread) => (
        <AIChatHistoryItem
          key={thread.id}
          thread={thread}
          isActive={thread.id === activeThreadId}
          onSelect={onSelectThread}
        />
      ))}
    </div>
  );
}
